import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Dropdown, { DropdownItem } from '../common/Dropdown/Dropdown';
import Icon from '../common/Icon';

import { ReactComponent as MetronomeIcon } from '../../icons/metronome-solid.svg';
import { ReactComponent as CaretIcon } from '../../icons/arrow-down.svg';

import { addTap, bpmFromTaps, MAX_PART_BPM, MIN_PART_BPM } from '../../utils/partTempo';

import './PartBpmPicker.scss';

/**
 * The tempo a part plays at, when it isn't the song's. Covers this part only — the next one is back
 * at the song's BPM. Portalled like the key picker beside it, since the toolbar scrolls sideways.
 */
const PartBpmPicker = ({ value, songBpm, disabled = false, title, onChange }) => {
  const { t } = useTranslation();
  const song = Number(songBpm);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [tapCount, setTapCount] = useState(0);
  const tapsRef = useRef([]);

  // Pointer down rather than click, so the time is taken when the finger lands
  const handleTap = (e) => {
    e.preventDefault();
    tapsRef.current = addTap(tapsRef.current, performance.now());
    setTapCount(tapsRef.current.length);

    const bpm = bpmFromTaps(tapsRef.current);
    if (bpm) setDraft(String(bpm));
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) return;

    tapsRef.current = [];
    setTapCount(0);
    setDraft(value ? String(value) : '');
  };

  // An out-of-range value leaves the menu open so it can be fixed
  const commitDraft = () => {
    const bpm = Math.round(Number(draft));
    if (!(bpm >= MIN_PART_BPM && bpm <= MAX_PART_BPM)) return;

    onChange(bpm === song ? '' : bpm);
    setOpen(false);
  };

  return (
    <Dropdown
      portal
      align="left"
      className="PartBpmPicker"
      triggerClassName={cx('bpm-trigger', { custom: !!value })}
      menuClassName="PartBpmMenu"
      title={title}
      ariaLabel={t('player.partTempo')}
      disabled={disabled}
      open={open}
      onOpenChange={handleOpenChange}
      trigger={
        <>
          <Icon icon={<MetronomeIcon aria-hidden="true" />} className="solid metronome" />
          <span className="value">{value || song || '--'}</span>
          <Icon icon={<CaretIcon aria-hidden="true" />} className="caret" />
        </>
      }
    >
      <DropdownItem
        className={cx('bpm-option', { on: !value })}
        subtitle={song ? t('player.bpm', { bpm: song }) : undefined}
        aria-pressed={!value}
        onClick={() => onChange('')}
      >
        {t('player.songTempo')}
      </DropdownItem>
      <button type="button" className={cx('tap-btn', { counting: tapCount > 0 })} onPointerDown={handleTap}>
        {t('player.tapTempo')}
      </button>
      {/* Not a form: the menu is portalled, but React still bubbles a submit up to the song form */}
      <div className="custom-bpm">
        <input
          type="number"
          inputMode="numeric"
          min={MIN_PART_BPM}
          max={MAX_PART_BPM}
          placeholder={t('player.custom')}
          aria-label={t('player.customBpm')}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            e.preventDefault();
            commitDraft();
          }}
        />
        <button type="button" className="set-btn" disabled={!draft} onClick={commitDraft}>
          {t('player.set')}
        </button>
      </div>
    </Dropdown>
  );
};

PartBpmPicker.propTypes = {
  /** The BPM the part declares, or empty when it plays at the song's */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  songBpm: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  disabled: PropTypes.bool,
  title: PropTypes.string,
  /** Given the chosen BPM, or an empty string for the song's tempo */
  onChange: PropTypes.func.isRequired,
};

export default PartBpmPicker;
